import { useState } from 'react'
import { parseISOToDateTime } from '@calendly/datetime'
import { Button } from '@calendly/ui/components/button'
import { ViewIcon } from '@calendly/ui/components/icons'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { Onetrust } from '@calendly/onetrust/redux'

import { useI18n } from 'locales'
import { formatDate } from 'services/spot_time'
import { formatAmount } from 'utils/currency'
import { computeInvoiceTotals, discountRowLabel, getInvoiceStatus, INVOICE_STATUS } from 'utils/invoice-helpers'
import { useParams } from 'react-router-dom'
import { useGetProfileQuery, useGetFeaturesQuery } from 'root-api'

import { useTrackInvoicePageView } from 'hooks/useTrackInvoicePageView'
import { BrandLogo } from 'components/layout/brand_logo'

import { useInvoicePageData } from './InvoiceContext'
import InvoiceDocument from './InvoiceDocument'
import InvoiceLineItems from './InvoiceLineItems'
import InvoiceMobileBillingGrid from './InvoiceMobileBillingGrid'
import InvoiceMobileHero from './InvoiceMobileHero'
import InvoiceMobilePaidSuccess from './InvoiceMobilePaidSuccess'
import InvoicePaymentForm from './InvoicePaymentForm'
import { useInvoicePrintScope } from './useInvoicePrintScope'
import './invoice-print-scope.css'
import {
  PageContainer,
  ContentWrapper,
  LeftColumn,
  RightColumn,
  RightColumnContent,
  PhoneInvoicePayCta,
  PhoneDownloadInvoiceCta,
  PhonePaymentSummary,
  PhonePaymentSummaryTitle,
  PhonePaymentSummarySubtitle,
  PhoneViewInvoiceButton,
  Footer,
  LogoWrapper,
  FooterLinks,
  FooterLink,
  InvoiceHeader,
  InvoiceHeaderImageContainer,
  InvoiceHeaderImage,
  InvoicePrintExclude,
  InvoiceLeftColumnContent,
  InvoicePaidWatermark,
  InvoicePaidStamp,
  InvoiceVoidedWatermark,
  InvoiceVoidedStamp,
  InvoiceDetails,
  ScreenOnlyDocument,
  PrintOnlyDocument,
} from './styles'

const isSafeHeaderImageUrl = (value) => {
  if (typeof value !== 'string') {
    return false
  }

  const normalizedValue = value.trim()
  if (!normalizedValue) {
    return false
  }

  try {
    return new URL(normalizedValue).protocol === 'https:'
  } catch {
    return false
  }
}

const InvoicePage = () => {
  const { invoice } = useInvoicePageData()
  useTrackInvoicePageView(invoice)
  useInvoicePrintScope()
  const { i18n } = useI18n()
  const isPhone = useDeviceType('isPhone')
  const isTabletDown = useDeviceType('isTabletDown')

  const [phoneMobileView, setPhoneMobileView] = useState('invoice')
  const [isMobileJustPaid, setIsMobileJustPaid] = useState(false)
  const [hasOpenedPaymentView, setHasOpenedPaymentView] = useState(false)
  const { profileSlug } = useParams()
  const { data: profile } = useGetProfileQuery({ slug: profileSlug }, { skip: !profileSlug })
  const { data: features } = useGetFeaturesQuery(
    { uuid: profile?.organization_uuid },
    { skip: !profile?.organization_uuid },
  )

  const dateLargeFormat = i18n.t('time.formats.date_large')
  const createdAtFormatted = invoice?.created_at
    ? formatDate(
      parseISOToDateTime(invoice.created_at),
      dateLargeFormat,
      i18n.locale,
    )
    : ''
  const dueAtFormatted = invoice?.due_at
    ? formatDate(parseISOToDateTime(invoice.due_at), dateLargeFormat, i18n.locale)
    : ''
  const paidAtRaw = invoice?.paid_at || invoice?.updated_at || invoice?.created_at
  const paidAtFormatted = paidAtRaw
    ? formatDate(parseISOToDateTime(paidAtRaw), dateLargeFormat, i18n.locale)
    : ''

  const billerBusinessName = invoice?.invoice_biller_info?.business_name?.trim()
  const currency = invoice?.invoice_settings?.currency || 'USD'
  const lineItems = [...(invoice?.line_items || [])].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  )
  const {
    subtotalCents,
    discountTotalCents,
    taxTotalCents,
    totalCents,
    parsedTaxRate,
  } = computeInvoiceTotals(invoice)
  const taxName = invoice?.invoice_settings?.tax_name?.trim() || i18n.t('booking.invoice.tax')
  const discountLabel = discountRowLabel(
    invoice?.discount_line_items,
    i18n.t('booking.invoice.discount'),
  )
  const amountDueValue = formatAmount(totalCents / 100, currency)
  const invoiceNotes = invoice?.notes?.trim()
  const headerImageSrc = isSafeHeaderImageUrl(invoice?.header_image_url)
    ? invoice.header_image_url
    : null
  const invoiceStatus = getInvoiceStatus(invoice)
  const isPaid = invoiceStatus === INVOICE_STATUS.PAID
  const isVoided = invoiceStatus === INVOICE_STATUS.VOIDED
  const isPastDue = invoiceStatus === INVOICE_STATUS.PAST_DUE
  const isNonPayable = isPaid || isVoided

  const scrollPageToTop = () => {
    document.body.scrollTop = 0
  }

  const handleOpenPaymentView = () => {
    setIsMobileJustPaid(false)
    setHasOpenedPaymentView(true)
    setPhoneMobileView('payment')
    window.requestAnimationFrame(scrollPageToTop)
  }

  const handlePaymentSuccess = () => {
    setIsMobileJustPaid(true)
    setPhoneMobileView('invoice')
    window.requestAnimationFrame(scrollPageToTop)
  }

  const handleDownloadInvoice = () => {
    window.print()
  }

  const showMobilePaidSuccess = isPhone && isPaid && isMobileJustPaid

  const invoiceColumnHidden = isPhone && phoneMobileView === 'payment'
  const paymentColumnHidden = isPhone && phoneMobileView === 'invoice'

  const documentProps = {
    invoice,
    headerImageSrc,
    isPaid,
    billerBusinessName,
    createdAtFormatted,
    dueAtFormatted,
    paidAtFormatted,
    amountDueValue,
    lineItems,
    currency,
    subtotalCents,
    discountTotalCents,
    discountLabel,
    taxTotalCents,
    totalCents,
    parsedTaxRate,
    taxName,
    invoiceNotes,
    isVoided,
    isPastDue,
  }

  const renderInvoiceBody = () => {
    if (!isPhone) {
      return <InvoiceDocument {...documentProps} />
    }

    return (
      <>
        <ScreenOnlyDocument data-testid="invoice-screen-document">
          {showMobilePaidSuccess ? (
            <InvoiceMobilePaidSuccess
              invoice={invoice}
              amountDueValue={amountDueValue}
              paidAtFormatted={paidAtFormatted}
              onDownloadInvoice={handleDownloadInvoice}
            />
          ) : (
            <>
              <InvoiceHeader>
                {headerImageSrc && (
                  <InvoiceHeaderImageContainer>
                    <InvoiceHeaderImage
                      src={headerImageSrc}
                      alt=""
                      width={131}
                      height={87}
                    />
                  </InvoiceHeaderImageContainer>
                )}
              </InvoiceHeader>
              <InvoiceMobileHero
                invoice={invoice}
                amountDueValue={amountDueValue}
                paidAtFormatted={paidAtFormatted}
                dueAtFormatted={dueAtFormatted}
                isPaid={isPaid}
                isVoided={isVoided}
                isPastDue={isPastDue}
              />
              <InvoiceDetails>
                <InvoiceMobileBillingGrid
                  invoice={invoice}
                  billerBusinessName={billerBusinessName}
                  createdAtFormatted={createdAtFormatted}
                  dueAtFormatted={dueAtFormatted}
                />
              </InvoiceDetails>
              <InvoiceLineItems
                lineItems={lineItems}
                currency={currency}
                isPhone
                subtotalCents={subtotalCents}
                discountTotalCents={discountTotalCents}
                discountLabel={discountLabel}
                taxTotalCents={taxTotalCents}
                totalCents={totalCents}
                parsedTaxRate={parsedTaxRate}
                taxName={taxName}
                invoiceNotes={invoiceNotes}
                isVoided={isVoided}
              />
            </>
          )}
        </ScreenOnlyDocument>
        <PrintOnlyDocument data-testid="invoice-print-document">
          <InvoiceDocument {...documentProps} />
        </PrintOnlyDocument>
      </>
    )
  }

  const renderFooter = () => (
    <InvoicePrintExclude>
      <Footer data-testid="invoice-footer">
        <LogoWrapper>
          <BrandLogo size="16px" tintLegacy />
        </LogoWrapper>
        <FooterLinks>
          {features?.onetrust && (
            <FooterLink type="button" onClick={() => window.OneTrust?.ToggleInfoDisplay()}>
              Manage cookies
            </FooterLink>
          )}
          <FooterLink type="button">Report</FooterLink>
        </FooterLinks>
      </Footer>
    </InvoicePrintExclude>
  )

  return (
    <PageContainer>
      {features?.onetrust && (
        <Onetrust
          isEnabled
          domainScript={features.onetrust.data_domain_script}
          hide={features.onetrust.hide}
        />
      )}
      <ContentWrapper paidInvoiceLayout={isNonPayable}>
        <LeftColumn data-col="invoice" data-hidden={invoiceColumnHidden ? 'true' : 'false'}>
          {isPaid ? (
            <InvoicePaidWatermark aria-hidden data-testid="invoice-paid-watermark">
              <InvoicePaidStamp>{i18n.t('booking.invoice.paid')}</InvoicePaidStamp>
            </InvoicePaidWatermark>
          ) : null}
          {isVoided ? (
            <InvoiceVoidedWatermark aria-hidden data-testid="invoice-voided-watermark">
              <InvoiceVoidedStamp>{i18n.t('booking.invoice.void_watermark')}</InvoiceVoidedStamp>
            </InvoiceVoidedWatermark>
          ) : null}
          <InvoiceLeftColumnContent>
            {renderInvoiceBody()}
          </InvoiceLeftColumnContent>
          {(!isTabletDown || isNonPayable || (isPhone && phoneMobileView === 'invoice')) && renderFooter()}

          {isPhone && !isNonPayable && (
            <PhoneInvoicePayCta>
              <Button wide onClick={handleOpenPaymentView}>
                {i18n.t('booking.invoice.pay_amount', { amount: amountDueValue })}
              </Button>
            </PhoneInvoicePayCta>
          )}

          {isPhone && isNonPayable && !showMobilePaidSuccess && (
            <InvoicePrintExclude>
              <PhoneDownloadInvoiceCta data-testid="phone-download-invoice-cta">
                <Button wide appearance="secondary-outline" onClick={handleDownloadInvoice}>
                  {i18n.t('booking.invoice.download_invoice')}
                </Button>
              </PhoneDownloadInvoiceCta>
            </InvoicePrintExclude>
          )}
        </LeftColumn>

        {/* Lazy-mount on phone: avoid firing stripe_payment_intents POST until user taps Pay */}
        {!isNonPayable && (!isPhone || hasOpenedPaymentView) && (
          <RightColumn data-col="payment" data-hidden={paymentColumnHidden ? 'true' : 'false'}>
            {isPhone && (
            <PhonePaymentSummary>
              {headerImageSrc ? (
                <InvoiceHeaderImage src={headerImageSrc} alt="" width={131} height={87} />
              ) : null}
                <PhonePaymentSummaryTitle>
                  {i18n.t('booking.invoice.invoice_from_biller', {
                    invoice_number: invoice.invoice_number,
                    biller_name: invoice?.invoice_biller_info?.name || '',
                  })}
                </PhonePaymentSummaryTitle>
                {dueAtFormatted && (
                  <PhonePaymentSummarySubtitle>
                    {i18n.t('booking.invoice.amount_due_on', {
                      amount: amountDueValue,
                      date: dueAtFormatted,
                    })}
                  </PhonePaymentSummarySubtitle>
                )}
                <PhoneViewInvoiceButton
                  type="button"
                  onClick={() => setPhoneMobileView('invoice')}
                >
                  <ViewIcon size="standard" aria-hidden />
                  {i18n.t('booking.invoice.view_invoice')}
                </PhoneViewInvoiceButton>
              </PhonePaymentSummary>
            )}
            <RightColumnContent>
              <InvoicePaymentForm
                submitButtonLabel={
                  isPhone
                    ? i18n.t('booking.invoice.pay_amount', { amount: amountDueValue })
                    : undefined
                }
                onPaymentSuccess={handlePaymentSuccess}
              />
              {isTabletDown && !(isPhone && phoneMobileView === 'invoice') && renderFooter()}
            </RightColumnContent>
          </RightColumn>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default InvoicePage
